import React from 'react';

import { useFormik } from 'formik';
import { useGetGlobalTncQuery, useGetLocalTncQuery } from 'store/terms-and-conditions/termsAPI';
import styled from '@emotion/styled';
import * as yup from 'yup';
import { selectCreatePrsStep1Values } from 'store/post-results-services/prsSlice';
import { useAppSelector } from 'store';
import { FormFooter } from 'components';
import DOMPurify from 'dompurify';

import { useFormFields } from '@britishcouncil/react-forms';
import { PageLoader, TnCCheckbox } from '@britishcouncil/react-solas-ors3';
import { Button } from '@britishcouncil/react-solas';

import { useCreatePrsSubmit } from '../useCreatePrsSubmit';

import * as S from './Step2.style';
import { StyledMessage } from './Step2.style';

export type CreatePRSStep2Fields = {
  tncAccepted: boolean;
};

type Step2Props = {
  onBack: (data: CreatePRSStep2Fields) => void;
  onNext: (data: CreatePRSStep2Fields) => void;
  initialValues: CreatePRSStep2Fields;
};

export const Step2 = ({ initialValues, onNext, onBack }: Step2Props) => {
  const step1Values = useAppSelector(selectCreatePrsStep1Values);
  const { data: globalTnC, isFetching: globalTnCLoading } = useGetGlobalTncQuery(step1Values?.countryId || -1);
  const { data: localTnC, isFetching: localTnCLoading } = useGetLocalTncQuery(step1Values?.countryId || -1);
  const formik = useFormik<CreatePRSStep2Fields>({
    initialValues: initialValues,
    validationSchema: yup.object().shape({
      tncAccepted: yup.boolean().isTrue('Please accept terms and conditions.'),
    }),
    onSubmit: (values) => {
      onNext(values);
      submit();
    },
  });

  const fields = useFormFields(formik);
  const { submit, serverErrors, isSubmitting } = useCreatePrsSubmit();

  if (globalTnCLoading || localTnCLoading) {
    return <PageLoader />;
  }

  return (
    <S.Form onSubmit={formik.handleSubmit}>
      {!globalTnC?.text && !localTnC?.text ? (
        <>
          <StyledMessage variant="warning" hideIcon={true}>
            No terms and conditions.
          </StyledMessage>

          <S.ButtonContainer>
            <Button disabled={false} size="xs" intent="primary" onClick={() => onBack(formik.values)}>
              Back
            </Button>
          </S.ButtonContainer>
        </>
      ) : (
        <>
          <StyledTCContainer>
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(globalTnC?.text || '', {
                  ADD_ATTR: ['target'],
                }),
              }}
            />
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(localTnC?.text || '', {
                  ADD_ATTR: ['target'],
                }),
              }}
            />
          </StyledTCContainer>
          <TnCCheckbox {...fields.tncAccepted}>I accept Terms & Conditions</TnCCheckbox>
          <FormFooter mainErrors={!isSubmitting ? serverErrors : undefined} />

          <S.ButtonContainer>
            <Button disabled={false} size="xs" intent="primary" onClick={() => onBack(formik.values)}>
              Previous
            </Button>
            <Button disabled={isSubmitting || !formik.isValid} size="xs" type="submit" intent="default">
              Create
            </Button>
          </S.ButtonContainer>
        </>
      )}
    </S.Form>
  );
};

const StyledTCContainer = styled.div`
  background-color: #fff;
  padding: 20px;
  margin-bottom: 20px;
  word-break: break-word;
`;
